import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sun, Thermometer, Battery, Droplets, ArrowRight, CheckCircle, Zap, Plug, Car, Snowflake, Home, Volume2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { track } from "@/lib/analytics";
import { openOpenSolar } from "@/lib/widgets";

const services = [
  {
    icon: Sun,
    title: "Zonnepanelen",
    description: "Wek uw eigen duurzame energie op met hoogwaardige zonnepanelen. Bespaar tot 70% op uw energierekening.",
    image: "/lovable-uploads/977b5f1b-4d7f-41bb-a847-195f88d2e297.png",
    link: "/zonnepanelen",
    benefits: ["Tot 70% besparing", "25 jaar garantie", "Snelle terugverdientijd"]
  },
  {
    icon: Thermometer,
    title: "Warmtepompen",
    description: "Verwarm en koel uw woning efficiënt met onze moderne warmtepompsystemen. Tot 4x energiezuiniger.",
    image: "/lovable-uploads/0d632d82-b00f-4fb6-90b4-b8537a6271bf.png",
    link: "/warmtepompen",
    benefits: ["4x energiezuiniger", "Verwarmen & koelen", "Hoge subsidies"]
  },
  {
    icon: Battery,
    title: "Thuisbatterijen",
    description: "Sla uw opgewekte zonne-energie op en gebruik deze wanneer u wilt. Volledige onafhankelijkheid.",
    image: "/lovable-uploads/b83869e8-6359-4a3a-9bf1-902a12b2fda2.png",
    link: "/thuisbatterijen",
    benefits: ["24/7 eigen stroom", "Onafhankelijkheid", "Extra besparingen"]
  },
  {
    icon: Droplets,
    title: "Zonneboilers",
    description: "Warm water met zonne-energie. Milieuvriendelijk en kostenbesparend voor uw dagelijkse comfort.",
    image: "/lovable-uploads/206b9305-6f6f-41ed-8c93-640e9ba0583e.png",
    link: "/zonneboilers",
    benefits: ["Gratis warm water", "Milieuvriendelijk", "Lage onderhoudskosten"]
  },
  {
    icon: Car,
    title: "Laadpalen",
    description: "Laad uw elektrische auto slim en veilig thuis of op de zaak. Met load balancing, PV-sturing en 1-/3-fase opties.",
    image: "/lovable-uploads/ee8dcd5f-c93a-4b35-b115-4ee0c2f68f7e.png",
    link: "/laadpalen",
    benefits: ["Slim laden met zonnestroom", "Veilig 1- en 3-fase", "Load balancing"]
  },
  {
    icon: Plug,
    title: "Elektra & Verlichting",
    description: "Van meterkastvernieuwing tot energiezuinige verlichting binnen en buiten. Veilig, efficiënt en klaar voor de toekomst.",
    image: "/lovable-uploads/3b76f085-cc9a-47c7-ac28-0db1622d9086.png",
    link: "/elektra",
    benefits: ["Meterkast moderniseren", "LED & slimme schakelaars", "Tuin- en buitenverlichting"]
  },
  {
    icon: Snowflake,
    title: "Airco",
    description: "Comfortabel koelen én verwarmen met energiezuinige lucht-lucht warmtepompen.",
    image: "/lovable-uploads/1ca4c62b-9067-4df4-9d6d-6829dc9dc29c.png",
    link: "/airco",
    benefits: ["Koelen én verwarmen", "Fluisterstil", "Energiezuinig (A+++)"]
  },
  {
    icon: Home,
    title: "Smart Home",
    description: "Intelligent wooncomfort met Loxone domotica. Automatiseer verlichting, klimaat en beveiliging voor optimaal comfort.",
    image: "/lovable-uploads/9a1762f1-4182-4e2f-8090-c0f95de24ec3.png",
    link: "/smarthome",
    benefits: ["Intelligente automatisering", "Energiebesparing", "Comfort & veiligheid"]
  },
  {
    icon: Volume2,
    title: "Audio & Geluid",
    description: "Professionele audiosystemen voor binnen en buiten. Van multiroom systemen tot outdoor speakers voor ultiem luisterplezier.",
    image: "/lovable-uploads/815db32a-8762-4c66-aa95-d00500f2f887.png",
    link: "/audio",
    benefits: ["Multiroom audio", "Premium geluidskwaliteit", "Binnen & buiten systemen"]
  }
];

export function ServicesSection() {
  const isMobile = useIsMobile();

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background/90 to-primary/5"></div>
      <div className="container mx-auto px-4 relative">
        {/* Enhanced Header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/20 to-primary/10 text-primary px-6 py-3 rounded-full text-sm font-semibold mb-6 border border-primary/20">
            <Zap className="h-4 w-4" />
            Bespaar tot €2.500 per jaar
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
            Complete Energieoplossingen
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Van zonnepanelen tot smart home - ontdek onze volledige range duurzame oplossingen
          </p>
        </div>

        {/* Enhanced Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {services.map((service, index) => (
            <Card key={index} className="group relative border-0 bg-card/50 backdrop-blur-md shadow-xl hover:shadow-2xl transition-all duration-700 h-full overflow-hidden rounded-2xl">
              {/* Enhanced Image Container */}
              <div className="relative h-64 md:h-72 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10"></div>
                <img 
                  src={service.image} 
                  alt={`${service.title} - Duurzame energieoplossing door SOL-VI`}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                  loading="lazy"
                  width="400"
                  height="288"
                />
                
                {/* Enhanced Icon Badge */}
                <div className="absolute top-6 right-6 w-14 h-14 bg-white/95 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 z-20">
                  <service.icon className="h-7 w-7 text-primary" />
                </div>

                {/* Enhanced Benefits Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/90 to-primary/80 opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center p-8 z-30">
                  <div className="text-center text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <h4 className="font-bold text-lg mb-4 text-white">Belangrijkste voordelen:</h4>
                    <div className="space-y-3">
                      {service.benefits.map((benefit, i) => (
                        <div 
                          key={i} 
                          className="flex items-center gap-3 justify-center text-sm bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 transition-all duration-300"
                          style={{ transitionDelay: `${i * 100}ms` }}
                        >
                          <CheckCircle className="h-5 w-5 flex-shrink-0 text-white" />
                          <span className="font-medium text-white">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Content */}
              <CardContent className="p-8">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 group-hover:text-primary transition-colors duration-300 leading-tight">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8 line-clamp-3">
                  {service.description}
                </p>

                <Button 
                  variant="outline" 
                  size="lg"
                  className="w-full group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-500 border-2 font-semibold text-base py-3 rounded-xl" 
                  asChild
                >
                  <Link 
                    to={service.link}
                    onClick={() => track("service_card_click", { service: service.title })}
                    className="flex items-center justify-center"
                  >
                    Meer informatie
                    <ArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Professional Design Software Section - Simplified */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center mt-12 md:mt-20 mb-8 md:mb-12">
          <div className="order-2 lg:order-1">
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4">
              {isMobile ? "Professioneel Ontwerp" : "Professionele Ontwerpsoftware"}
            </h3>
            <p className="text-muted-foreground mb-4 md:mb-6 text-sm md:text-base lg:text-lg leading-relaxed">
              {isMobile 
                ? "Modernste ontwerp- en analysesoftware voor uw zonnepanelensysteem."
                : "Wij gebruiken de modernste ontwerp- en analysesoftware om uw zonnepanelensysteem tot in detail te plannen. Van luchtfoto-analyse tot 3D-visualisatie - u ziet precies hoe uw systeem eruit komt te zien."
              }
            </p>
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-center gap-2 md:gap-3">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                <span className="text-sm md:text-base">
                  {isMobile ? "Luchtfoto-analyse" : "Satellietbeelden en luchtfoto-analyse"}
                </span>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                <span className="text-sm md:text-base">3D-visualisatie van uw dak</span>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                <span className="text-sm md:text-base">
                  {isMobile ? "Schaduw- & opbrengstanalyse" : "Schaduwanalyse en opbrengstberekening"}
                </span>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-primary flex-shrink-0" />
                <span className="text-sm md:text-base">
                  {isMobile ? "Technische rapporten" : "Gedetailleerde technische rapporten"}
                </span>
              </div>
            </div>
          </div>
          <div className="relative order-1 lg:order-2">
            <img 
              src="/lovable-uploads/27c4bb85-0458-4f43-ba7e-3963d6900749.png" 
              alt="Professionele solar design software interface"
              className="rounded-lg shadow-lg w-full"
              loading="lazy"
              width="600"
              height="400"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
            <div className="absolute -bottom-2 md:-bottom-4 -right-2 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-primary/10 rounded-lg flex items-center justify-center">
              <Zap className="h-6 w-6 md:h-8 md:w-8 text-primary" />
            </div>
          </div>
        </div>

        <div className="text-center mt-8 md:mt-12">
          <Button 
            variant="hero" 
            size={isMobile ? "default" : "lg"} 
            onClick={() => { 
              track("services_cta_offerte");
              (window as any).requestIdleCallback ? (window as any).requestIdleCallback(() => {}) : null; 
              openOpenSolar(); 
            }} 
            className={`${isMobile ? "text-base px-6 py-3" : "text-lg px-8"} min-h-[48px]`}
            aria-label="Open OpenSolar offerteformulier"
          >
            {isMobile ? "Gratis Offerte" : "Vraag een Gratis Offerte aan"}
            <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
}